import { Link } from "react-router-dom";
import { ArrowRight, PenLine, FileSearch, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const stages = [
  { icon: PenLine, label: "Stage 01", title: "Write Your Book", to: "/write-your-book" },
  { icon: FileSearch, label: "Stage 02", title: "Manuscript Review", to: "/first-draft" },
  { icon: Sparkles, label: "Stage 03", title: "Extend", to: "/extend" },
];

const EditorialHero = () => {
  return (
    <section className="relative overflow-hidden pt-28 sm:pt-36 pb-16 sm:pb-24">
      <div
        className="absolute inset-0 -z-10 bg-cover bg-center opacity-[0.18]"
        style={{ backgroundImage: "url('/hero-bg.png')" }}
        aria-hidden
      />
      <div
        className="absolute inset-0 -z-10 bg-gradient-to-b from-background/60 via-background/90 to-background"
        aria-hidden
      />

      <div className="container mx-auto px-4">
        <div className="grid gap-14 lg:grid-cols-[1.45fr_1fr] lg:gap-20 lg:items-end">
          {/* Lead story */}
          <div className="animate-fade-in">
            <div className="flex items-center gap-3 mb-7">
              <span className="rule-gold" aria-hidden />
              <p className="eyebrow">AuthorPro · for experts who write</p>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl xl:text-[4.25rem] font-semibold leading-[1.05] mb-7">
              Write your Book, Review your Book,{" "}
              <span className="text-gold-gradient">Take your Book to the next level.</span>
            </h1>

            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl mb-10">
              Most business books end up on a shelf. We work with you across the whole arc — shaping
              the concept, reviewing the manuscript, then turning the finished book into an
              AI-powered platform that attracts clients and earns revenue long after publication.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                asChild
                size="lg"
                className="bg-gold text-gold-foreground hover:bg-gold-light font-semibold text-base px-8"
              >
                <a
                  href="https://meetings.series-a.co/meetings/series-a/authorpro"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2"
                >
                   Book a Call
                  <ArrowRight className="w-4 h-4" />
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-base px-8">
                <Link to="/sign-in">Sign In</Link>
              </Button>
            </div>
          </div>

          {/* Sidebar: the three stages */}
          <aside className="lg:border-l lg:border-border lg:pl-10">
            <p className="eyebrow mb-6">In this edition</p>
            <ul className="divide-y divide-border">
              {stages.map(({ icon: Icon, label, title, to }) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="group flex items-center gap-4 py-5 transition-colors"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm border border-border bg-card text-gold transition-colors group-hover:border-gold/50">
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                        {label}
                      </span>
                      <span className="block font-serif text-xl group-hover:text-gold transition-colors">
                        {title}
                      </span>
                    </span>
                    <ArrowRight className="ml-auto h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-gold" />
                  </Link>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default EditorialHero;
